
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Dream } from "@/types/database-extensions";

export const useDreams = () => {
  const [dreams, setDreams] = useState<Dream[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchDreams();
    }
  }, [user]);

  const fetchDreams = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch dreams
      const { data, error } = await supabase
        .from('dreams')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching dreams:", error);
        // Just set empty dreams array instead of showing error toast to user
        setDreams([]);
        return;
      }
      
      setDreams(data || []);
    } catch (error) {
      console.error("Error fetching dreams:", error);
      setDreams([]);
    } finally {
      setLoading(false);
    }
  };

  const addDream = async (title: string) => {
    if (!title.trim() || !user) return;
    
    try {
      // Add dream
      const { data, error } = await supabase
        .from('dreams')
        .insert([
          { 
            title: title,
            progress: 0,
            badges: [],
            user_id: user.id
          }
        ])
        .select()
        .single();
      
      if (error) {
        console.error("Error adding dream:", error);
        toast({
          title: "Error",
          description: "Could not add your dream",
          variant: "destructive"
        });
        return;
      }
      
      if (data) {
        setDreams([data, ...dreams]);
        
        toast({
          title: "Dream Added",
          description: "Your new dream has been added to your board",
        });

        return data;
      }
    } catch (error) {
      console.error("Error adding dream:", error);
      toast({
        title: "Error",
        description: "Could not add your dream",
        variant: "destructive"
      });
    }
  };

  const deleteDream = async (id: string) => {
    if (!user) return;
    
    try {
      // Delete dream
      const { error } = await supabase
        .from('dreams')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      
      if (error) {
        console.error("Error deleting dream:", error);
        toast({
          title: "Error",
          description: "Could not delete your dream",
          variant: "destructive"
        });
        return;
      }
      
      setDreams(dreams.filter(dream => dream.id !== id));
      
      toast({
        title: "Dream Removed",
        description: "Your dream has been removed from your board",
      });
    } catch (error) {
      console.error("Error deleting dream:", error);
      toast({
        title: "Error",
        description: "Could not delete your dream",
        variant: "destructive"
      });
    }
  };

  // Update user karma points
  const updateKarmaPoints = async (points: number) => {
    if (!user) return;
    
    try {
      // Get current karma points
      const { data, error } = await supabase
        .from('profiles')
        .select('karma_points')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error("Error getting karma points:", error);
        return;
      }
      
      const currentPoints = data?.karma_points || 0;
      const newPoints = currentPoints + points;
      
      // Update karma points
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ karma_points: newPoints })
        .eq('id', user.id);
        
      if (updateError) {
        console.error("Error updating karma points:", updateError);
        return;
      }
        
      // Show toast for karma points
      toast({
        title: `+${points} Karma Points!`,
        description: "Keep up the good work!",
      });
    } catch (error) {
      console.error("Error updating karma points:", error);
    }
  };

  return {
    dreams,
    loading,
    addDream,
    deleteDream,
    updateKarmaPoints
  };
};
